/* eslint-disable react/prop-types */
/* eslint-disable import/no-unresolved */
/* eslint-disable import/extensions */

import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { Oval } from 'react-loader-spinner';

import useOnClickOutside from '@/hooks/useClickOutside';
import { ReactComponent as DropIcon } from '@/assets/icons/nav-arrow-down.icon.svg';
import { device } from '@/constants/breakpoints';
import { fetchCashFlowChart } from '@/services/dashboardApi';
import { numberWithCommas } from '@/utils/helpers';

const durations = [
  {
    id: 1,
    name: 'This Year',
    type: 'year',
    value: new Date().getFullYear()
  },
  {
    id: 2,
    name: 'Previous Year',
    type: 'year',
    value: new Date().getFullYear() - 1
  },
  {
    id: 3,
    name: 'Last 12 Months',
    type: 'month',
    value: 12
  },
  {
    id: 4,
    name: 'Last 6 Months',
    type: 'month',
    value: 6
  }
];

function CustomTooltip({ active, payload, label }) {
  if (active && payload && payload.length) {
    return (
      <CustomedTooltip className="custom-tooltip">
        <p className="desc">{label}</p>
        <p className="label">{`Inflow: ₦${numberWithCommas(payload[1].value || '00.00')}`}</p>
        <p className="label">{`Outflow: ₦${numberWithCommas(payload[0].value || '00.00')}`}</p>
      </CustomedTooltip>
    );
  }

  return null;
}

function CashflowChart() {
  const [openDuration, setOpenDuration] = useState(false);
  const [dateType, setDateType] = useState(durations[2]);
  const ref = useRef();

  useOnClickOutside(ref, () => setOpenDuration(false));

  const cashFlowChart = useQuery({
    queryKey: ['cashFlowChart', { type: dateType?.type, yearOrNumberOfMonths: dateType?.value }],
    queryFn: fetchCashFlowChart,
    onSuccess: () => {},
    onError: () => {}
  });

  return (
    <CashflowChartStyles>
      <div className="chart-view">
        <div className="chart-header">
          <div className="chart-header__title">
            <h3>Cash flow</h3>
          </div>
          <div className="chart-header__legends">
            <div className="chart-header__legend">
              <div className="chart-header__legend__color1"></div>
              <div className="chart-header__legend__text">Inflows</div>
            </div>
            <div className="chart-header__legend">
              <div className="chart-header__legend__color2"></div>
              <div className="chart-header__legend__text">Outflows</div>
            </div>
          </div>
        </div>

        <div className="chart-duration">
          <button
            type="button"
            onClick={() => {
              setOpenDuration(true);
            }}
            className="drop">
            {dateType?.name} <DropIcon />
          </button>

          <AnimatePresence>
            {openDuration ? (
              <motion.div
                className="menu"
                ref={ref}
                initial={{ y: -20 }}
                animate={{ y: 0 }}
                exit={{ y: -10, opacity: 0 }}
                transition={{ duration: 0.2 }}>
                {durations.map((duration) => (
                  <button
                    type="button"
                    key={duration?.id}
                    onClick={() => {
                      setOpenDuration(false);
                      setDateType(duration);
                    }}>
                    {duration.name}
                  </button>
                ))}
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>

        {cashFlowChart?.isFetching ? (
          <div className="loader">
            <Oval color="#0EBE8E" height={40} width={40} />
          </div>
        ) : (
          <div className="chart-body">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                width={500}
                height={400}
                data={cashFlowChart?.data?.data?.chartData}
                margin={{
                  top: 10,
                  right: 20,
                  left: -10,
                  bottom: 0
                }}>
                <CartesianGrid strokeDasharray="1 1" horizontal={false} vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{
                    stroke: '#BFBFBF',
                    strokeWidth: 0.4,
                    fontSize: '0.7rem'
                  }}
                  tickFormatter={(tick) => tick.slice(0, 3)}
                />
                <YAxis
                  tick={{
                    stroke: '#BFBFBF',
                    strokeWidth: 0.4,
                    fontSize: '0.7rem'
                  }}
                  tickFormatter={(tick) => `${(tick / 1000000).toFixed(1)}M`}
                />
                <Tooltip
                  formatter={(value) => `${(value / 1000000).toFixed(1)}M`}
                  content={<CustomTooltip />}
                  wrapperStyle={{ outline: 'none' }}
                  offset={5}
                />
                <Area
                  type="monotone"
                  dataKey="outflow"
                  strokeWidth={3}
                  stroke="#F0468D"
                  fill="#FAC1D9"
                  stackId="1"
                />
                <Area
                  type="monotone"
                  dataKey="inflow"
                  strokeWidth={3}
                  stroke="#0EBE8E"
                  fill="#B8ECDE"
                  stackId="2"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      <div className="info-view">
        <div className="info-view__item">
          <h3 className="info-view__item__title">Opening</h3>
          <p className="info-view__item__desc">
            ₦{numberWithCommas(cashFlowChart?.data?.data?.totals?.openingBalance || '00.00')}
          </p>
        </div>
        <div className="info-view__item">
          <h3 className="info-view__item__title inflow">Inflows +</h3>
          <p className="info-view__item__desc">
            ₦{numberWithCommas(cashFlowChart?.data?.data?.totals?.totalInflow || '00.00')}
          </p>
        </div>
        <div className="info-view__item">
          <h3 className="info-view__item__title outflow">Outflows -</h3>
          <p className="info-view__item__desc">
            ₦{numberWithCommas(cashFlowChart?.data?.data?.totals?.totalOutflow || '00.00')}
          </p>
        </div>
        <div className="info-view__item">
          <h3 className="info-view__item__title">Closing</h3>
          <p className="info-view__item__desc">
            ₦{numberWithCommas(cashFlowChart?.data?.data?.totals?.closingBalance || '00.00')}
          </p>
        </div>
      </div>
    </CashflowChartStyles>
  );
}

export default CashflowChart;

const CashflowChartStyles = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  @media ${device.phone} {
    width: 100%;
    flex-direction: column-reverse;
  }

  .chart-view {
    width: 60%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    padding-bottom: 20px;
    border-bottom: 1px solid ${({ theme }) => theme.colors?.layer};

    @media ${device.phone} {
      width: 100%;
    }

    .chart-header {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;

      .chart-header__title {
        h3 {
          font-size: 1rem;
          font-weight: 600;
          color: ${({ theme }) => theme.colors?.secondary};
        }
      }

      .chart-header__legends {
        display: flex;
        align-items: center;
        justify-content: flex-end;

        .chart-header__legend {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          margin-left: 20px;

          .chart-header__legend__color1 {
            width: 13px;
            height: 13px;
            background-color: ${({ theme }) => theme.colors?.primary};
            border-radius: 50%;
            margin-right: 10px;
          }

          .chart-header__legend__color2 {
            width: 13px;
            height: 13px;
            background-color: ${({ theme }) => theme.colors?.error};
            border-radius: 50%;
            margin-right: 10px;
          }

          .chart-header__legend__text {
            font-size: 0.875rem;
            font-weight: 400;
            color: ${({ theme }) => theme.colors?.secondary};
          }
        }
      }
    }

    .chart-duration {
      margin-top: 20px;
      position: relative;
      .drop {
        width: 210px;
        height: 40px;
        border-radius: 4px;
        background-color: ${({ theme }) => theme.colors?.inputBackground};
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 12px;
        color: ${({ theme }) => theme.colors?.secondary};
      }

      .menu {
        position: absolute;
        top: 45px;
        right: 0px;
        background-color: ${({ theme }) => theme.colors?.white};
        box-shadow: 0px 3px 14px rgba(0, 0, 0, 0.05);
        border-radius: 8px;
        z-index: 100;
        width: 210px;
        overflow: hidden;

        button {
          padding: 16px;
          font-size: 0.875rem;
          background-color: transparent;
          transition: all 0.2s ease-in-out;
          width: 100%;
          border: none;
          outline: none;
          border-radius: 0;
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: flex-start;
          color: ${({ theme }) => theme.colors?.secondary};

          &:hover {
            background-color: ${({ theme }) => theme.colors?.layer};
          }
        }
      }
    }

    .chart-body {
      width: 100%;
      height: 250px;
      background-color: ${({ theme }) => theme.colors?.white};
      border-radius: 12px;
      box-shadow: 0px 18.2453px 54.736px rgba(0, 0, 0, 0.05);
      margin-top: 20px;
      padding: 20px 0;
    }

    .loader {
      width: 100%;
      height: 250px;
      background-color: ${({ theme }) => theme.colors?.white};
      border-radius: 12px;
      box-shadow: 0px 18.2453px 54.736px rgba(0, 0, 0, 0.05);
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  .info-view {
    width: 35%;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: flex-start;
    margin-top: 50px;

    @media ${device.phone} {
      width: 100%;
      margin-bottom: 30px;
      flex-direction: row;
      margin-top: 20px;
      flex-wrap: wrap;
    }

    .info-view__item {
      width: 100%;
      margin-bottom: 20px;
      display: flex;
      flex-direction: column;
      align-items: flex-end;

      @media ${device.phone} {
        align-items: flex-start;
        width: 48%;
      }

      .info-view__item__title {
        font-size: 0.875rem;
        font-weight: 400;
        color: ${({ theme }) => theme.colors?.secondary};
        margin-right: 10px;
        text-align: right;
        margin-right: 0;
        margin-bottom: 5px;

        @media ${device.phone} {
          margin-right: 0;
        }
      }

      .inflow {
        color: #0ebe8e;
      }

      .outflow {
        color: ${({ theme }) => theme.colors?.error};
      }

      .info-view__item__desc {
        font-size: 1.4rem;
        font-weight: 600;
        color: ${({ theme }) => theme.colors?.secondary};

        @media ${device.phone} {
          font-size: 1rem;
        }
      }
    }
  }
`;

const CustomedTooltip = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.colors?.white};
  border-radius: 12px;
  border: none !important;
  outline: none !important;
  box-shadow:
    0px 4.7644px 4.7644px rgba(50, 50, 71, 0.08),
    0px 4.7644px 9.5288px rgba(50, 50, 71, 0.06);
  padding: 12px;

  .desc {
    font-size: 0.8rem;
    margin-bottom: 4px;
  }

  .label {
    font-size: 1rem;
    color: ${({ theme }) => theme.colors?.activeTitle};
    font-weight: 600;
    margin-top: 3px;
  }
`;
